import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  MoreVertical,
  Play,
  Pause,
  Archive,
  Eye,
  AlertCircle,
  X,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { CampaignStatusPill } from '@/components/facebook/CampaignStatusPill';

// ─── Status pill ─────────────────────────────────────────────────────────────
// Wraps the existing CampaignStatusPill to keep palette consistency.
export const StatusPill: React.FC<{ status: string; size?: 'default' | 'small' }> = ({
  status,
  size = 'default',
}) => {
  // size hint kept for API parity with the reference; CampaignStatusPill is already compact.
  return (
    <span className={clsx(size === 'small' && 'text-[10px]')}>
      <CampaignStatusPill status={status ?? 'UNKNOWN'} />
    </span>
  );
};

// ─── Objective pill ──────────────────────────────────────────────────────────
export const ObjectivePill: React.FC<{ objective?: string | null }> = ({ objective }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-surface-alt border border-border text-text-secondary">
    {(objective ?? '').replace('OUTCOME_', '') || '—'}
  </span>
);

// ─── Instagram glyph (lucide@1.11 lacks Instagram) ───────────────────────────
export const InstagramGlyph: React.FC<{ size?: number; className?: string }> = ({
  size = 14,
  className,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.5" y2="6.5" />
  </svg>
);

// ─── Facebook glyph (lucide@1.11 lacks Facebook too) ─────────────────────────
export const FacebookGlyph: React.FC<{ size?: number; className?: string }> = ({
  size = 14,
  className,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M22 12a10 10 0 1 0-11.563 9.879v-6.987H7.898V12h2.539V9.797c0-2.506 1.493-3.89 3.776-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.892h-2.33v6.987A10.002 10.002 0 0 0 22 12Z" />
  </svg>
);

// ─── Platform icons ──────────────────────────────────────────────────────────
// Slightercrm campaigns list doesn't currently track explicit publisher
// platforms per ad — the picker UI defaults to FB+IG. Render both badges for
// now; the AdCard variant takes explicit booleans.
export const PlatformIcons: React.FC<{
  hasFacebook?: boolean;
  hasInstagram?: boolean;
  className?: string;
}> = ({ hasFacebook = true, hasInstagram = true, className }) => (
  <div className={clsx('flex items-center gap-1', className)}>
    {hasFacebook && (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#1877F2] text-white">
        <FacebookGlyph size={11} />
      </span>
    )}
    {hasInstagram && (
      <span
        className="flex h-5 w-5 items-center justify-center rounded-full text-white"
        style={{
          backgroundImage:
            'linear-gradient(135deg, #515BD4 0%, #8134AF 25%, #DD2A7B 50%, #FEDA77 100%)',
        }}
      >
        <InstagramGlyph size={11} />
      </span>
    )}
  </div>
);

// ─── Inline error alert ──────────────────────────────────────────────────────
export const InlineErrorAlert: React.FC<{ message: string; onDismiss?: () => void }> = ({
  message,
  onDismiss,
}) => (
  <div className="mt-2 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-xs text-danger">
    <AlertCircle size={13} className="mt-0.5 shrink-0" />
    <span className="flex-1">{message}</span>
    {onDismiss && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
        className="rounded p-0.5 hover:bg-danger/10"
        aria-label="Dismiss"
      >
        <X size={13} />
      </button>
    )}
  </div>
);

// ─── ActionsDropdown ─────────────────────────────────────────────────────────

export type EntityType = 'campaign' | 'adset' | 'ad';

export interface ActionsDropdownProps {
  entityType: EntityType;
  currentStatus: string;
  isLoading?: boolean;
  onView?: () => void;
  onToggleStatus?: () => void;
  /** Force-refresh insights from Meta for this entity. Currently wired only
   *  for campaigns — backend supports per-campaign refresh via
   *  GET /campaigns/:id?refresh=1. */
  onSyncInsights?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
}

export const ActionsDropdown: React.FC<ActionsDropdownProps> = ({
  entityType,
  currentStatus,
  isLoading,
  onView,
  onToggleStatus,
  onSyncInsights,
  onArchive,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const status = (currentStatus ?? '').toUpperCase();
  const isActive = status === 'ACTIVE';
  const isPaused =
    status === 'PAUSED' ||
    status === 'CAMPAIGN_PAUSED' ||
    status === 'ADSET_PAUSED' ||
    status === 'AD_PAUSED';
  const isArchived = status === 'ARCHIVED' || status === 'DELETED';
  const canToggle = (isActive || isPaused) && !!onToggleStatus;

  const noun = entityType === 'campaign' ? 'Campaign' : entityType === 'adset' ? 'Ad Set' : 'Ad';

  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        disabled={isLoading}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={clsx(
          'flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors',
          'hover:bg-surface-alt hover:text-text-primary disabled:opacity-50',
        )}
        aria-label={`${noun} actions`}
      >
        <MoreVertical size={15} />
      </button>
      {open && (
        <div
          className={clsx(
            'absolute right-0 top-full z-30 mt-1 w-48 overflow-hidden rounded-xl border border-border bg-surface shadow-elevated',
            'animate-fade-in',
          )}
        >
          {onView && (
            <MenuItem
              onClick={() => {
                setOpen(false);
                onView();
              }}
              icon={<Eye size={14} />}
            >
              View details
            </MenuItem>
          )}
          {canToggle && (
            <MenuItem
              onClick={() => {
                setOpen(false);
                onToggleStatus?.();
              }}
              icon={isActive ? <Pause size={14} /> : <Play size={14} />}
            >
              {isActive ? `Pause ${noun}` : `Resume ${noun}`}
            </MenuItem>
          )}
          {onSyncInsights && (
            <MenuItem
              onClick={() => {
                setOpen(false);
                onSyncInsights();
              }}
              icon={<RefreshCw size={14} />}
            >
              Sync insights
            </MenuItem>
          )}
          {onArchive && !isArchived && (
            <>
              {(onView || canToggle || onSyncInsights) && <div className="border-t border-border" />}
              <MenuItem
                onClick={() => {
                  setOpen(false);
                  onArchive();
                }}
                icon={<Archive size={14} />}
                danger
              >
                Archive {noun}
              </MenuItem>
            </>
          )}
          {onDelete && (
            <>
              {(onView || canToggle || onSyncInsights || (onArchive && !isArchived)) && <div className="border-t border-border" />}
              <MenuItem
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
                icon={<Trash2 size={14} />}
                danger
              >
                Delete {noun}
              </MenuItem>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const MenuItem: React.FC<{
  onClick: () => void;
  icon?: React.ReactNode;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ onClick, icon, danger, children }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={clsx(
      'flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium transition-colors',
      danger
        ? 'text-danger hover:bg-danger-soft'
        : 'text-text-secondary hover:bg-surface-alt hover:text-text-primary',
    )}
  >
    {icon}
    <span>{children}</span>
  </button>
);
